import { action, computed, observable, makeObservable } from 'mobx'
import type { Location } from 'react-router-dom'
import NavigationStore from '../NavigationStore'
import { QuoteSearchRequest } from 'src/viewModels/QuoteSearchRequest'
import Constants from '../../config'
import { IOrganization, IUser } from '../../Definitions'
import { SimpleEntityDto } from '../../generated_client'
import { OrganizationId } from '../../utils/OrganizationIds'
import { ClaimsAcquisitionSearchRequest } from 'src/viewModels/ClaimsAcquisitionSearchRequest'
import { DashboardSearchRequest } from 'src/viewModels/DashboardSearchRequest'
import { BillOutSearchRequest } from 'src/viewModels/BillOutSearchRequest'
import { ReceivedCheckSearchRequest } from 'src/viewModels/ReceivedCheckSearchRequest'
import { SentCheckSearchRequest } from 'src/viewModels/SentCheckSearchRequest'
import { InboxSummary } from 'src/viewModels/workflow/InboxSummary'
import { PayProvidersSearchRequest } from 'src/viewModels/PayProvidersSearchRequest'
import { ProviderRefundsSearchRequest } from 'src/viewModels/ProviderRefundsSearchRequest'
import { InboxItemType } from 'src/utils/InboxItemType'
import { FixedInboxSearchRequest } from 'src/viewModels/workflow/FixedInboxFilterRequest'

class GlobalStore {
  @computed
  public get isLoggedIn() {
    return !!this.user
  }

  @computed
  public get isOrganizationSwitchAvailable() {
    if (this.canSwitchOrganizations && this.canSwitchOrganizations === true) {
      return true
    }
    return false
  }

  @computed
  public get isAdmin() {
    if (this.user) {
      return this.user.roles.indexOf(Constants.ObxRoles.Administrator) !== -1
    }
    return false
  }

  @computed
  public get isWorkflowAdmin() {
    if (this.user) {
      return this.user.roles.indexOf(Constants.ObxRoles.WorkflowAdmin) !== -1
    }
    return false
  }

  @computed
  public get isCurrentOrganizationSurgical(): boolean {
    if (
      this.currentAppOrganization &&
      this.currentAppOrganization.id === OrganizationId.Surgical
    ) {
      return true
    }
    return false
  }

  @computed
  public get currentUserOrganization(): SimpleEntityDto {
    if (this.user) {
      return this.user.organization as SimpleEntityDto
    }
    return {} as SimpleEntityDto
  }

  @computed
  public get drawerWidth() {
    return this.isDrawerOpen ? 255 : 72
  }

  @observable
  public canSwitchOrganizations: boolean = false

  @observable
  public user?: IUser

  @observable
  public isDrawerOpen: boolean = false

  @observable
  public isToggleDrawerTooltipOpen = false

  @observable
  public currentAppOrganization?: IOrganization

  @observable
  public isSurgical: boolean = true

  @observable
  public isDarkMode: boolean = false

  @observable
  public quoteRequest?: QuoteSearchRequest

  @observable
  public checkRequest?: ReceivedCheckSearchRequest

  @observable
  public sentCheckRequest?: SentCheckSearchRequest

  @observable
  public acqRequest?: ClaimsAcquisitionSearchRequest

  @observable
  public billOutRequest?: BillOutSearchRequest

  @observable
  public receivePaymentRequest?: BillOutSearchRequest

  @observable
  public dashboardRequest?: DashboardSearchRequest

  @observable
  public payProviderRequest?: PayProvidersSearchRequest

  @observable
  public providerRefundRequest?: ProviderRefundsSearchRequest

  @observable
  public selectedInbox?: InboxSummary

  @observable
  public selectedInboxOrder?: string

  @observable
  public selectedInboxOrderColumn?: string

  @observable
  public returnRoutePath?: string

  @observable
  public selectedInboxType?: InboxItemType

  @observable
  public savedFixedInboxFilter?: FixedInboxSearchRequest

  private navigationStore: NavigationStore
  constructor(navigationStore: NavigationStore) {
    makeObservable(this)
    this.navigationStore = navigationStore
  }

  @action.bound
  public toggleDrawer() {
    this.isDrawerOpen = !this.isDrawerOpen
    this.isToggleDrawerTooltipOpen = false
  }

  @action.bound
  public toggleDrawerToolTip() {
    this.isToggleDrawerTooltipOpen = !this.isToggleDrawerTooltipOpen
  }

  @action.bound
  public setUser(user: IUser) {
    this.user = user
  }

  @action.bound
  public setCanSwitchOrganizations(canSwitch: boolean) {
    this.canSwitchOrganizations = canSwitch
  }

  @action.bound
  public clearUser() {
    this.user = undefined
  }

  @action.bound
  public setCurrentAppOrganization(organization?: IOrganization) {
    this.currentAppOrganization = organization
  }

  @action.bound
  public changeTheme(id: string | undefined) {
    this.isSurgical = id === OrganizationId.Surgical ? true : false
  }

  @action.bound
  public setQuoteRequest(request: QuoteSearchRequest) {
    this.quoteRequest = request
  }

  @action.bound
  public setCheckRequest(request: ReceivedCheckSearchRequest) {
    this.checkRequest = request
  }

  @action.bound
  public setSentCheckRequest(request: SentCheckSearchRequest) {
    this.sentCheckRequest = request
  }

  @action.bound
  public setBillOutRequest(request: BillOutSearchRequest) {
    this.billOutRequest = request
  }

  @action.bound
  public setReceivePaymentRequest(request: BillOutSearchRequest) {
    this.receivePaymentRequest = request
  }

  @action.bound
  public setAcqRequest(request: ClaimsAcquisitionSearchRequest) {
    this.acqRequest = request
  }

  @action.bound
  public setDashboardRequest(request: DashboardSearchRequest) {
    this.dashboardRequest = request
  }

  @action.bound
  public setPayProviderRequest(request: PayProvidersSearchRequest) {
    this.payProviderRequest = request
  }

  @action.bound
  public setProviderRefundRequest(request: ProviderRefundsSearchRequest) {
    this.providerRefundRequest = request
  }

  @action.bound
  public setSelectedInbox(inbox: InboxSummary | undefined) {
    this.selectedInbox = inbox
  }

  @action.bound
  public setSelectedInboxType(type: InboxItemType | undefined) {
    this.selectedInboxType = type
  }

  @action.bound
  public setSelectedInboxSort(order: string | undefined, orderBy: string | undefined) {
    this.selectedInboxOrder = order
    this.selectedInboxOrderColumn = orderBy
  }

  @action.bound
  public setFixedImportRequest(request: FixedInboxSearchRequest) {
    this.savedFixedInboxFilter = request
  }

  @action.bound
  public toggleDarkMode() {
    this.isDarkMode = !this.isDarkMode
    localStorage.setItem('darkMode', JSON.stringify(this.isDarkMode))
  }

  @action.bound
  public initializeDarkMode() {
    const savedMode = localStorage.getItem('darkMode')
    if (savedMode !== null) {
      this.isDarkMode = JSON.parse(savedMode)
    }
  }

  @action.bound
  public async setReturn(path?: string | undefined) {
    this.returnRoutePath = path === undefined ? 'new tab' : path
  }

  @action.bound
  public closeTab(fallBackPath?: string | undefined) {
    if (this.returnRoutePath === 'new tab') {
      window.close()
      this.returnRoutePath = undefined
    } else if (this.returnRoutePath !== undefined) {
      this.navigationStore.push(this.returnRoutePath)
      this.returnRoutePath = undefined
    } else if (fallBackPath) {
      this.navigationStore.push(fallBackPath)
    }
  }

  @action.bound
  public rerouteToPath(path: string) {
    this.navigationStore.push(path)
  }

  // @ts-ignore
  @action.bound
  public rerouteIfNotLoggedIn(location?: Location) {
    if (!location) {
      location = this.navigationStore?.location ?? undefined
    }
    if (
      !this.isLoggedIn &&
      location &&
      location.pathname !== '/login' &&
      location.pathname !== '/magic-link' &&
      location.pathname !== '/login/magic-link'
    ) {
      const previousRoute = btoa(location.pathname + location.search)
      this.navigationStore.push('/login/magic-link?previousRoute=' + previousRoute)
    }
  }

  @action.bound
  public clearUserAndRouteToLogin() {
    this.clearUser()
    this.quoteRequest = undefined
    this.billOutRequest = undefined
    this.acqRequest = undefined
    this.dashboardRequest = undefined
    this.checkRequest = undefined
    this.payProviderRequest = undefined
    this.providerRefundRequest = undefined
    this.selectedInbox = undefined
    this.selectedInboxOrder = undefined
    this.selectedInboxOrderColumn = undefined
    this.selectedInboxType = undefined
    this.rerouteIfNotLoggedIn()
  }
}

export default GlobalStore
